/**
 * <ActivationResumeDateSection> — « Date de reprise », dans ses deux rendus (organism).
 *
 * C'est la section qui compte DEUX fois dans les neuf du legacy, parce qu'elle a deux écritures
 * (`ActivationComposerScreen.js:903-955`), plus une que le legacy n'a pas :
 *
 * - **au choix** — un choix fermé parmi les dates proposées par le back, rendu seulement s'il en
 *   reste PLUS D'UNE : un sélecteur réduit à une option déjà retenue est un choix mort ;
 * - **libre** — `minDate` donné (après un stage de l'année) : n'importe quel jour à partir de
 *   cette date, au calendrier ;
 * - **verrouillée** — plus aucune date proposable (catalogue épuisé) : la date s'affiche en
 *   lecture seule, jamais un sélecteur vide ou mort à 100 %.
 *
 * `lockedLine` l'emporte : c'est le view-model qui sait laquelle des deux écritures s'applique,
 * il ne reste ici aucune règle. La mention de pause n'accompagne que le rendu verrouillé — c'est
 * la pause qui porte alors cette date, et c'est là qu'il faut aller la changer.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { ActivationChoiceSection } from "../ActivationChoiceSection"
import { Card } from "../../molecules/Card"
import { DatePicker } from "../../molecules/DatePicker"
import { Icon } from "../../atoms/icons/Icon"
import { List } from "../../molecules/List"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { type UIActivationChoice } from "../../../ports/primary/ui/types"

const TITLE = "Date de reprise"

export type ActivationResumeDateSectionProps = {
    /** Les dates proposées. Moins de deux → la section ne rend rien. */
    options?: UIActivationChoice[]
    /** Date libre : la première choisissable. Donnée, la section rend un calendrier, pas les options. */
    minDate?: Date | null
    value?: string | null
    onChange?: (value: string) => void
    /** Date verrouillée, déjà formulée — « Reprise le 1ᵉʳ septembre 2026 ». Elle l'emporte. */
    lockedLine?: string | null
    /** « Date portée par la pause — pour la changer, modifiez la pause. » */
    lockedNote?: string | null
    testID?: string
}

export function ActivationResumeDateSection(props: ActivationResumeDateSectionProps) {
    const { options = [], minDate, value, onChange, lockedLine, lockedNote, testID } = props

    if (lockedLine != null && lockedLine !== "") {
        return (
            <List title={TITLE} titleIcon="calendar-alt" testID={testID}>
                <Card p="3">
                    <Stack gap="1">
                        <Row gap="2" align="start">
                            <Icon name="lock" size={16} color="text-muted" />
                            <Text weight="semibold" style={styles.line}>
                                {lockedLine}
                            </Text>
                        </Row>
                        {lockedNote != null && lockedNote !== "" && (
                            <Text size="text-sm" color="text-muted">
                                {lockedNote}
                            </Text>
                        )}
                    </Stack>
                </Card>
            </List>
        )
    }

    if (minDate != null && onChange != null) {
        return (
            <List title={TITLE} titleIcon="calendar-alt" testID={testID}>
                <DatePicker value={value ?? ""} onChange={onChange} minDate={minDate} modalLabel={TITLE} />
            </List>
        )
    }

    // Une seule date proposée est une date imposée : on ne la fait pas « choisir ».
    if (options.length < 2 || onChange == null) return null

    return (
        <ActivationChoiceSection
            title={TITLE}
            iconName="calendar-alt"
            options={options}
            value={value}
            onChange={onChange}
            testID={testID}
        />
    )
}

const styles = StyleSheet.create(() => ({
    /** La date se replie plutôt que de pousser le cadenas hors de la carte. */
    line: { flexShrink: 1 },
}))
